"use client";

import { Box, CircularProgress, Link, styled, Stack } from "@mui/material";
import { useState } from "react";
import { Avatar } from "@/ui";
import isPlaceholderPhoto from "@lib/candidates/isPlaceholderPhoto";
import AvatarLibrary from "@components/shared/AvatarLibrary/AvatarLibrary";

const ImageContainer = styled(Box)({
  width: "200px",
  height: "200px",
  borderRadius: 6,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export interface ImageUploadProps {
  imageUrl?: string;
  /** Candidate name, used for the initials fallback when there's no photo. */
  name?: string;
  onUpdate?: (file: File) => void;
  onSelectFromLibrary?: (selection: { url: string; source: string }) => void;
}

export default function ImageUpload({
  imageUrl,
  name,
  onUpdate,
  onSelectFromLibrary,
}: ImageUploadProps) {
  const [imageLoading, setImageLoading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const handleLibraryClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setLibraryOpen(true);
  };

  const handleLibrarySelect = (selection: { url: string; source: string }) => {
    setImageLoading(true);
    onSelectFromLibrary?.(selection);
  };

  const handleLibraryUpload = (file: File) => {
    setImageLoading(true);
    onUpdate?.(file);
  };

  // Clear the loading spinner once a new image URL arrives (React's replacement
  // for a set-state-in-effect).
  const [prevImageUrl, setPrevImageUrl] = useState(imageUrl);
  if (imageUrl !== prevImageUrl) {
    setPrevImageUrl(imageUrl);
    setImageLoading(false);
  }

  // Legacy d=mm gravatar URLs resolve to gravatar's grey silhouette whatever
  // the address. Treat one as "no photo" so this matches the initials shown in
  // the candidate list.
  const photoUrl = isPlaceholderPhoto(imageUrl) ? undefined : imageUrl;

  return (
    <Stack direction="column" alignItems="center" spacing={2}>
      <ImageContainer>
        {imageLoading ? (
          <CircularProgress size={80} color="secondary" />
        ) : (
          <Avatar
            src={photoUrl}
            name={name}
            alt="Profile photo"
            variant="rounded"
            style={{ width: "100%", height: "100%", fontSize: 56 }}
          />
        )}
      </ImageContainer>
      {onSelectFromLibrary && !imageLoading && (
        <Link href="#" onClick={handleLibraryClick}>
          Update avatar
        </Link>
      )}
      <AvatarLibrary
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleLibrarySelect}
        onUpload={handleLibraryUpload}
        name={name}
      />
    </Stack>
  );
}
